import type {ComposerSelection} from './composerFormatting';

const FENCE = '```';

export type OpenCodeBlock = {
    start: number;
    language: string;
    bodyStarted: boolean;
};

export type CodeBlockState = OpenCodeBlock | null;

export type CodeBlockChange = {
    text: string;
    selection: ComposerSelection;
    block: CodeBlockState;
    rewritten: boolean;
};

type Edit = {
    at: number;
    removed: number;
    inserted: string;
};

function collapsed(at: number): ComposerSelection {
    return {start: at, end: at};
}

export function diffEdit(previous: string, next: string): Edit {
    const limit = Math.min(previous.length, next.length);
    let prefix = 0;
    while (prefix < limit && previous[prefix] === next[prefix]) {
        prefix += 1;
    }
    let suffix = 0;
    while (
        suffix < limit - prefix &&
        previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]
    ) {
        suffix += 1;
    }
    return {
        at: prefix,
        removed: previous.length - prefix - suffix,
        inserted: next.slice(prefix, next.length - suffix),
    };
}

function isLineStart(text: string, at: number): boolean {
    return at === 0 || text[at - 1] === '\n';
}

function restOfLineIsEmpty(text: string, at: number): boolean {
    const lineBreak = text.indexOf('\n', at);
    return (lineBreak === -1 ? text.length : lineBreak) === at;
}

function fenceJustCompleted(text: string, edit: Edit): boolean {
    const caret = edit.at + edit.inserted.length;
    return (
        edit.inserted.length > 0 &&
        !edit.inserted.includes('\n') &&
        caret >= FENCE.length &&
        text.slice(caret - FENCE.length, caret) === FENCE &&
        isLineStart(text, caret - FENCE.length) &&
        restOfLineIsEmpty(text, caret)
    );
}

function endsOutsideAFencedRun(segment: string): boolean {
    let inside = false;
    for (const line of segment.replace(/\r\n/g, '\n').split('\n')) {
        if (inside) {
            if (line.trim() === FENCE) {
                inside = false;
            }
        } else if (line.startsWith(FENCE)) {
            inside = true;
        }
    }
    return !inside;
}

function openedFence(
    previousText: string,
    text: string,
    edit: Edit,
): {start: number; language: string} | null {
    if (
        edit.inserted.length === 0 ||
        edit.inserted.includes('\r') ||
        edit.inserted.indexOf('\n') !== edit.inserted.length - 1
    ) {
        return null;
    }
    if (previousText.slice(edit.at, edit.at + edit.removed).includes('\n')) {
        return null;
    }
    const caret = edit.at + edit.inserted.length;
    if (caret !== text.length) {
        return null;
    }
    const start = text.lastIndexOf('\n', caret - 2) + 1;
    const line = /^```([^\s`]*)$/.exec(text.slice(start, caret - 1));
    if (!line) {
        return null;
    }
    if (!endsOutsideAFencedRun(text.slice(0, start))) {
        return null;
    }
    return {start, language: line[1]};
}

function renderBlock(language: string, body: string): string {
    return `${FENCE}${language}\n${body}\n${FENCE}`;
}

export function closeCodeBlock(
    text: string,
    block: OpenCodeBlock,
): {text: string; selection: ComposerSelection} {
    const rendered = renderBlock(block.language, text.slice(block.start));
    return {
        text: text.slice(0, block.start) + rendered,
        selection: collapsed(block.start + rendered.length),
    };
}

export function rebaseOntoRewrite(
    rawText: string,
    rewrittenText: string,
    nextText: string,
): string | null {
    const edit = diffEdit(rawText, rewrittenText);
    if (edit.inserted !== '' || edit.removed === 0) {
        return null;
    }
    const end = edit.at + edit.removed;
    if (nextText.slice(0, end) !== rawText.slice(0, end)) {
        return null;
    }
    return nextText.slice(0, edit.at) + nextText.slice(end);
}

export function reduceCodeBlockChange(
    previousText: string,
    block: CodeBlockState,
    nextText: string,
): CodeBlockChange {
    const edit = diffEdit(previousText, nextText);
    const caret = edit.at + edit.inserted.length;
    const unchanged = (next: CodeBlockState): CodeBlockChange => ({
        text: nextText,
        selection: collapsed(caret),
        block: next,
        rewritten: false,
    });

    if (!block) {
        const opened = openedFence(previousText, nextText, edit);
        if (!opened) {
            return unchanged(null);
        }
        return {
            text: nextText.slice(0, opened.start) + nextText.slice(caret),
            selection: collapsed(opened.start),
            block: {...opened, bodyStarted: true},
            rewritten: true,
        };
    }

    const {start, language} = block;

    if (edit.at < start) {
        if (edit.at + edit.removed >= start) {
            return unchanged(null);
        }
        return unchanged({
            ...block,
            start: start + edit.inserted.length - edit.removed,
        });
    }

    if (edit.removed > 0 && edit.inserted === '' && nextText.length <= start) {
        return unchanged(null);
    }

    if (
        edit.inserted === '\n' &&
        edit.removed === 0 &&
        edit.at === start &&
        nextText.length === caret
    ) {
        if (language === '') {
            return {
                text: nextText.slice(0, start),
                selection: collapsed(start),
                block: null,
                rewritten: true,
            };
        }
        const rendered = renderBlock(language, '');
        return {
            text: nextText.slice(0, start) + rendered + '\n',
            selection: collapsed(start + rendered.length + 1),
            block: null,
            rewritten: true,
        };
    }

    if (
        edit.inserted === '\n' &&
        edit.removed === 0 &&
        edit.at > start &&
        nextText[edit.at - 1] === '\n'
    ) {
        const rendered = renderBlock(language, nextText.slice(start, edit.at - 1));
        return {
            text: nextText.slice(0, start) + rendered + '\n' + nextText.slice(caret),
            selection: collapsed(start + rendered.length + 1),
            block: null,
            rewritten: true,
        };
    }

    if (fenceJustCompleted(nextText, edit) && caret - FENCE.length >= start) {
        const fenceLineStart = caret - FENCE.length;
        const bodyEnd = fenceLineStart === start ? start : fenceLineStart - 1;
        const rendered = renderBlock(language, nextText.slice(start, bodyEnd));
        return {
            text: nextText.slice(0, start) + rendered + nextText.slice(caret),
            selection: collapsed(start + rendered.length),
            block: null,
            rewritten: true,
        };
    }

    return unchanged(block);
}
